import { getChromeRuntime } from './wrappers';
import { ProcessUrlResponse, ProcessUrlResponseType } from '../types';
import { getDownloadUrl } from '../constants';
import { getRemoteBlockerBaseUrl } from '../navigation/remoteBlockerUrl';

export const BLOCK_PAGE_TYPE = {
  WEBSITE_BLOCKED_DEFAULT_RULES: 'website_blocked_default_rules',
  TIME_RESTRICTION_RANGE: 'time_restriction_range',
  TIME_RESTRICTION_QUOTA: 'time_restriction_quota',
  PAUSE_INTERNET: 'pause_internet',
  WEB_SITE_BLOCKED_ROUTINES_RULES: 'web_site_blocked_routines_rules',
  SCREEN_FREE_TIME_ROUTINES_RULES: 'screen_free_time_routines_rules',
} as const;

export type BlockPageType =
  (typeof BLOCK_PAGE_TYPE)[keyof typeof BLOCK_PAGE_TYPE];

const buildBlockerPageUrl = (blockPageType: BlockPageType): string => {
  const base = getRemoteBlockerBaseUrl();
  if (!base) {
    return '';
  }
  return `${base}index.html?block_page_type=${blockPageType}&hide_manage_button=true`;
};

export const getBlockedByQuotaScreenUrl = () =>
  buildBlockerPageUrl(BLOCK_PAGE_TYPE.TIME_RESTRICTION_QUOTA);

export const getBlockedByCategoryUrl = () =>
  buildBlockerPageUrl(BLOCK_PAGE_TYPE.WEBSITE_BLOCKED_DEFAULT_RULES);

export const getScheduleRestrictedScreenUrl = () =>
  buildBlockerPageUrl(BLOCK_PAGE_TYPE.TIME_RESTRICTION_RANGE);

export const getBlockedByPauseScreenUrl = () =>
  buildBlockerPageUrl(BLOCK_PAGE_TYPE.PAUSE_INTERNET);

export const getBlockedByScreenFreeTimeRoutineUrl = () =>
  buildBlockerPageUrl(BLOCK_PAGE_TYPE.SCREEN_FREE_TIME_ROUTINES_RULES);

export const getBlockedByContentFilteringRoutineUrl = () =>
  buildBlockerPageUrl(BLOCK_PAGE_TYPE.WEB_SITE_BLOCKED_ROUTINES_RULES);

/**
 * Centralizes blocker routing so every blocking verdict variant stays tied to a
 * single remote screen. The `never` branch keeps new variants from silently
 * falling back to no blocker.
 */
export const getBlockedScreenUrl = (
  result: ProcessUrlResponse
): string | undefined => {
  switch (result.type) {
    case ProcessUrlResponseType.BLOCK:
      return getBlockedByCategoryUrl();
    case ProcessUrlResponseType.BLOCK_BY_QUOTA:
      return getBlockedByQuotaScreenUrl();
    case ProcessUrlResponseType.BLOCK_BY_SCHEDULE:
      return getScheduleRestrictedScreenUrl();
    case ProcessUrlResponseType.BLOCK_BY_PAUSE:
      return getBlockedByPauseScreenUrl();
    case ProcessUrlResponseType.BLOCK_BY_SCREEN_FREE_TIME_ROUTINE:
      return getBlockedByScreenFreeTimeRoutineUrl();
    case ProcessUrlResponseType.BLOCK_BY_CONTENT_FILTERING_ROUTINE:
      return getBlockedByContentFilteringRoutineUrl();
    case ProcessUrlResponseType.ALLOW:
    case ProcessUrlResponseType.PENDING:
    case ProcessUrlResponseType.IGNORE:
    case ProcessUrlResponseType.REDIRECT_TO_SAFE_SEARCH:
      return undefined;
    default:
      return assertNeverVerdict(result);
  }
};

const assertNeverVerdict = (result: never): undefined => {
  console.warn('Unhandled ProcessUrlResponse variant', result);
  return undefined;
};

export const matchesAuthorizationUrl = (url: string) =>
  url.indexOf(getDownloadUrl()) !== -1;

export const getOnboardingUrl = () =>
  getChromeRuntime().getURL('onboarding.html');

export const getSetupCompleteUrl = () =>
  getChromeRuntime().getURL('onboarding.html#/setup-complete');
